import * as React from "react";
import { Link, LinkProps } from "react-router-dom";
import styled, { css } from "styled-components";

import { SvgSprite } from "./SvgSprite";

import classNames from "classnames";

import { ParseRemark } from "@skv/core/src/ui";
import { OpportunitySearchQueryState } from "@skv/state";
import { FAVOURITES_ENUM } from "@skv/state/src/state/favourites";
import { getDateRange, isInThePast, resizeImg } from "@skv/util";
import { formatDistanceToNow } from "date-fns";
import { BaseFavouriteBtn, BaseFavouriteBtnProps, EventBadge } from "./";
import {
  VolunteerSearchLink,
  VolunteerSearchLinkProps,
} from "./VolunteerSearchLink";

const ResultListSvgSprite = styled(SvgSprite)`
  vertical-align: middle;
`;

export const ResultCardHeader = styled.header.attrs((props) => ({
  ...props,
  className: props.className || "mb-2",
}))``;

export const ResultCard = styled.article<{
  $isFeatured?: boolean;
  $isNew?: boolean;
  hover?: boolean;
}>`
  position: relative;
  margin-top: -1px;
  overflow-wrap: break-word;

  --result-card-hover-bg: ${(p) => p.theme.gray100};
  --primary-color: ${(p) => p.theme.primaryColor};
  --accent-color: ${(p) => p.theme.accentColor};
  --md-breakpoint: ${(p) => p.theme.mdBreakpoint};

  li > & {
    border-top: 1px solid ${(p) => p.theme.gray300};
  }

  li:not(:last-child) > & {
    border-bottom: 1px solid ${(p) => p.theme.gray300};
  }

  ${(p) =>
    p.hover !== false &&
    css`
      &:hover {
        background-color: var(--result-card-hover-bg);
        margin-left: -1rem;
        margin-right: -1rem;
        padding-left: 1rem;
        padding-right: 1rem;
      }
    `}

  ${(p) => {
    if (p.$isFeatured) {
      return css`
        padding-left: 0.5rem;
        margin-left: -0.5rem;
        box-shadow: -2px 0 0 var(--primary-color);

        @media (min-width: var(--md-breakpoint)) {
          padding-left: 1rem;
          margin-left: -1rem;
        }

        ${ResultCardHeader}::before {
          content: "In Demand";
          display: inline-flex;
          float: right;
          padding: 0 0 0.5rem 0.5rem;
          font-size: 0.875rem;
          color: var(--primary-color);
        }
      `;
    }

    if (p.$isNew) {
      return css`
        ${ResultCardHeader}::before {
          content: "New";
          display: inline-flex;
          float: right;
          padding: 0 0 0.5rem 0.5rem;
          font-size: 0.875rem;
          color: var(--accent-color);
        }
      `;
    }
  }}
`;

const SearchResultStretchedLink = styled(Link)<{
  to: LinkProps["to"];
}>`
  &:visited {
    color: ${(p) => p.theme.primaryColor};
  }
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

export const ResultCardHeading = styled.h3.attrs(({ className, ...rest }) => ({
  ...rest,
  className: className || "h4 mb-0",
}))``;

type HeadingProps = React.HTMLAttributes<HTMLHeadingElement>;

export interface ResultCardLinkHeadingProps
  extends Omit<HeadingProps, "title" | "css"> {
  title: React.ReactNode;
  to: LinkProps["to"];
  stretchedLink?: boolean;
  agencyName?: string | null;
}

export const ResultCardLinkHeading: React.FC<ResultCardLinkHeadingProps> = ({
  title,
  to,
  stretchedLink = true,
  agencyName,
  ...rest
}) => {
  return (
    <ResultCardHeading {...rest}>
      {stretchedLink ? (
        <SearchResultStretchedLink to={to}>{title}</SearchResultStretchedLink>
      ) : (
        <Link to={to}>{title}</Link>
      )}
      {!!agencyName && (
        <div className="d-block mb-1">
          <span className="h6 font-italic font-weight-normal">
            supported by
          </span>{" "}
          <span className="h6 font-weight-bold">{agencyName}</span>
        </div>
      )}
    </ResultCardHeading>
  );
};

export interface ResultCardVolunteerSearchLinkHeadingProps
  extends Omit<HeadingProps, "title" | "css"> {
  title: React.ReactNode;
  to: Exclude<VolunteerSearchLinkProps["to"], undefined>;
  searchQuery: OpportunitySearchQueryState;
  stretchedLink?: boolean;
  shouldUpdateSearchForm?: VolunteerSearchLinkProps["shouldUpdateSearchForm"];
}

export const ResultCardVolunteerSearchLinkHeading: React.FC<
  ResultCardVolunteerSearchLinkHeadingProps
> = ({
  title,
  to,
  searchQuery,
  stretchedLink = true,
  shouldUpdateSearchForm,
  ...rest
}) => {
  return (
    <ResultCardHeading {...rest}>
      {stretchedLink ? (
        <SearchResultStretchedLink
          as={VolunteerSearchLink}
          to={to}
          searchQuery={searchQuery}
          shouldUpdateSearchForm={shouldUpdateSearchForm}
        >
          <>{title}</>
        </SearchResultStretchedLink>
      ) : (
        <VolunteerSearchLink
          to={to}
          searchQuery={searchQuery}
          shouldUpdateSearchForm={shouldUpdateSearchForm}
        >
          <>{title}</>
        </VolunteerSearchLink>
      )}
    </ResultCardHeading>
  );
};

export const ResultCardSubHeading = styled.p.attrs(
  ({ className, ...rest }) => ({
    ...rest,
    className: className || "h5 mb-0",
  })
)``;

interface ResultCardLinkSubHeadingProps
  extends Omit<HeadingProps, "title" | "css"> {
  title: React.ReactNode;
  to?: LinkProps["to"];
  agencyName?: string;
  organisationMemberName?: string;
  isOrgLinkedEntity?: boolean;
}

export const ResultCardLinkSubHeading: React.FC<
  ResultCardLinkSubHeadingProps
> = ({
  title,
  to,
  agencyName,
  organisationMemberName,
  isOrgLinkedEntity,
  ...rest
}) => {
  return (
    <ResultCardSubHeading {...rest}>
      {organisationMemberName || agencyName ? (
        <>
          {isOrgLinkedEntity === true ? (
            <>
              {title}{" "}
              <span className="h6">
                <i className="font-weight-normal">supported by</i> {agencyName}
              </span>
            </>
          ) : (
            <>
              {organisationMemberName}{" "}
              <span className="h6">
                <i className="font-weight-normal">managed by</i> {title}
              </span>
            </>
          )}
        </>
      ) : (
        <>{title}</>
      )}
    </ResultCardSubHeading>
  );
};

interface BaseResultMetaListItemProps
  extends React.LiHTMLAttributes<HTMLLIElement> {
  small?: boolean;
}

export const BaseResultMetaListItem = styled.li.attrs<BaseResultMetaListItemProps>(
  ({ small = true, className, ...props }: BaseResultMetaListItemProps) => ({
    className: classNames(
      "list-inline-item",
      {
        small: small,
      },
      className
    ),
    ...props,
  })
)`
  margin-bottom: 0.5rem;
`;

type LiElementProps = React.DetailedHTMLProps<
  React.LiHTMLAttributes<HTMLLIElement>,
  HTMLLIElement
>;

interface ResultMetaListItemProps extends LiElementProps {
  icon?: "marker" | "ribbon" | "briefcase" | "calendar";
  small?: boolean;
}

export const ResultMetaListItem: React.FC<ResultMetaListItemProps> = ({
  children,
  icon,
  small,
  className,
}) => {
  const altText =
    icon === "marker"
      ? "Location: "
      : icon === "ribbon"
      ? "Category: "
      : icon === "briefcase"
      ? "Types of work: "
      : "Event date";

  return (
    <BaseResultMetaListItem small={small} className={className}>
      {icon && (
        <ResultListSvgSprite
          id={`icons:${icon}`}
          width={16}
          height={16}
          aria-label={altText ?? undefined}
          className="mr-1 align-text-top"
        />
      )}
      {children}
    </BaseResultMetaListItem>
  );
};

interface ResultMetaListProps {
  className?: string;
  $listItemSpacing?: string;
}

export const ResultMetaList = styled.ul.attrs((props: ResultMetaListProps) => ({
  ...props,
  className: classNames("list-inline", props.className),
}))`
  ${BaseResultMetaListItem}:not(:last-child) {
    margin-right: ${(p) => p.$listItemSpacing || "1rem"};
  }

  margin-bottom: 0;
`;

/* TODO : Revisit this isolated ml-n3 class */
export const ResultCardFooter = styled.footer.attrs((props) => ({
  ...props,
  className: props.className || "ml-n3 d-flex align-items-center",
}))``;

/*
  Full components that are built from ResultCard
*/

interface OpportunityResultCardProps {
  name: React.ReactNode;
  to: ResultCardLinkHeadingProps["to"];

  organisationName: React.ReactNode;
  organisationTo: ResultCardLinkSubHeadingProps["to"];

  agencyName?: string;
  organisationMemberName?: string;
  isOrgLinkedEntity?: boolean;

  locations?: React.ReactNode;
  category?: React.ReactNode;
  typesOfWork?: React.ReactNode;

  summary: string;

  logoUrl?: string;
  isFeatured?: boolean;
  isNew?: boolean;
  eventStartDate?: string;
  eventEndDate?: string;

  isFavourited: boolean;
  onFavourite: BaseFavouriteBtnProps["onFavourite"];
  onUnfavourite: BaseFavouriteBtnProps["onUnfavourite"];

  headingProps?: Partial<Parameters<typeof ResultCardLinkHeading>[0]>;
  subHeadingProps?: Partial<Parameters<typeof ResultCardLinkSubHeading>[0]>;
}

export const OpportunityResultCard: React.FC<OpportunityResultCardProps> = ({
  name,
  to,
  organisationName,
  organisationTo,
  organisationMemberName,
  agencyName,
  isOrgLinkedEntity,
  locations,
  category,
  typesOfWork,
  summary,
  logoUrl,
  isFeatured = false,
  isNew = false,
  eventStartDate,
  eventEndDate,

  isFavourited,

  headingProps,
  subHeadingProps,

  onFavourite,
  onUnfavourite,
}) => {
  return (
    <ResultCard $isFeatured={isFeatured} $isNew={isNew} className="pt-4 pb-1">
      <ResultCardHeader>
        <ResultCardLinkHeading title={name} to={to} {...headingProps} />
        <ResultCardLinkSubHeading
          title={organisationName}
          to={organisationTo}
          organisationMemberName={organisationMemberName}
          agencyName={agencyName}
          isOrgLinkedEntity={isOrgLinkedEntity}
          {...subHeadingProps}
        />
      </ResultCardHeader>

      {eventStartDate && (
        <ResultMetaList $listItemSpacing="3rem">
          {isInThePast(eventStartDate) === false && (
            <ResultMetaListItem>
              <EventBadge date={eventStartDate} />
            </ResultMetaListItem>
          )}
          <ResultMetaListItem icon="calendar">
            {getDateRange(eventStartDate, eventEndDate)}
          </ResultMetaListItem>
        </ResultMetaList>
      )}

      <ResultMetaList $listItemSpacing="3rem">
        {locations && (
          <ResultMetaListItem icon="marker">{locations}</ResultMetaListItem>
        )}
        {category && (
          <ResultMetaListItem icon="ribbon">{category}</ResultMetaListItem>
        )}
        {typesOfWork && (
          <ResultMetaListItem icon="briefcase">
            {typesOfWork}
          </ResultMetaListItem>
        )}
      </ResultMetaList>

      <ParseRemark>{summary}</ParseRemark>

      <ResultCardFooter>
        <BaseFavouriteBtn
          isFavourited={isFavourited}
          onFavourite={onFavourite}
          onUnfavourite={onUnfavourite}
          type={FAVOURITES_ENUM.OPPORTUNITY}
        />
        {logoUrl && (
          <img
            className="img-fluid my-2 ml-auto"
            srcSet={`${resizeImg(logoUrl, "180x120")} 2x`}
            src={resizeImg(logoUrl, "90x60")}
            alt={`Logo for ${organisationName}`}
          />
        )}
      </ResultCardFooter>
    </ResultCard>
  );
};

interface OrganisationResultCardProps {
  organisationName: string;
  to: ResultCardLinkHeadingProps["to"];
  category: React.ReactNode;
  headingProps?: Partial<Parameters<typeof ResultCardLinkHeading>[0]>;
  agencyName?: string | null;
}

export const OrganisationResultCard: React.FC<OrganisationResultCardProps> = ({
  organisationName,
  to,
  category,
  headingProps,
  agencyName,
}) => {
  return (
    <ResultCard className="py-3">
      <ResultCardHeader>
        <ResultCardLinkHeading
          title={organisationName}
          to={to}
          agencyName={agencyName}
          {...headingProps}
        />
      </ResultCardHeader>
      <ResultMetaList>
        {category && (
          <ResultMetaListItem icon="ribbon">{category}</ResultMetaListItem>
        )}
      </ResultMetaList>
    </ResultCard>
  );
};

interface OrganisationFavouriteResultCardProps {
  organisationName: React.ReactNode;
  to: ResultCardLinkHeadingProps["to"];

  locations?: React.ReactNode;
  category?: React.ReactNode;

  isFavourited: boolean;
  onFavourite: BaseFavouriteBtnProps["onFavourite"];
  onUnfavourite: BaseFavouriteBtnProps["onUnfavourite"];

  logoUrl?: string;
  summary: string;

  headingProps?: Partial<Parameters<typeof ResultCardLinkHeading>[0]>;
}

export const OrganisationFavouriteResultCard: React.FC<
  OrganisationFavouriteResultCardProps
> = ({
  to,
  isFavourited,
  onFavourite,
  onUnfavourite,
  organisationName,
  locations,
  category,
  logoUrl,
  summary,
  headingProps,
}) => {
  return (
    <ResultCard className="py-3">
      <ResultCardHeader>
        <ResultCardLinkHeading
          title={organisationName}
          to={to}
          {...headingProps}
        />
      </ResultCardHeader>
      <ResultMetaList>
        {locations && (
          <ResultMetaListItem icon="marker">{locations}</ResultMetaListItem>
        )}
        {category && (
          <ResultMetaListItem icon="ribbon">{category}</ResultMetaListItem>
        )}
      </ResultMetaList>

      <ParseRemark>{summary}</ParseRemark>

      <ResultCardFooter>
        <BaseFavouriteBtn
          isFavourited={isFavourited}
          onFavourite={onFavourite}
          onUnfavourite={onUnfavourite}
          type={FAVOURITES_ENUM.ORGANISATION}
        />
        {logoUrl && (
          <img
            className="img-fluid my-2 ml-auto"
            srcSet={`${resizeImg(logoUrl, "180x120")} 2x`}
            src={resizeImg(logoUrl, "90x60")}
            alt={`Logo for ${organisationName}`}
          />
        )}
      </ResultCardFooter>
    </ResultCard>
  );
};

interface OpportunityFavouriteResultCardProps {
  name: React.ReactNode;
  to: ResultCardLinkHeadingProps["to"];
  organisationName: React.ReactNode;

  locations?: React.ReactNode;
  category?: React.ReactNode;
  typesOfWork?: React.ReactNode;

  summary: string;

  logoUrl?: string;

  isFavourited: boolean;
  onFavourite: BaseFavouriteBtnProps["onFavourite"];
  onUnfavourite: BaseFavouriteBtnProps["onUnfavourite"];

  headingProps?: Partial<Parameters<typeof ResultCardLinkHeading>[0]>;
  subHeadingProps?: Partial<Parameters<typeof ResultCardLinkSubHeading>[0]>;
  date?: string;
}

export const OpportunityFavouriteResultCard: React.FC<
  OpportunityFavouriteResultCardProps
> = ({
  name,
  to,
  organisationName,
  locations,
  category,
  typesOfWork,
  summary,
  logoUrl,
  isFavourited,
  onFavourite,
  onUnfavourite,
  headingProps,
  subHeadingProps,
  date,
}) => {
  const formatDateDistance = (dateString: string) => {
    const formattedDate = formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
    });
    return formattedDate;
  };

  return (
    <ResultCard className="py-3">
      <ResultCardHeader>
        {date ? (
          <div className="d-flex align-items-center justify-content-between">
            <ResultCardLinkHeading title={name} to={to} {...headingProps} />
            <span className="text-dark">{formatDateDistance(date)}</span>
          </div>
        ) : (
          <ResultCardLinkHeading title={name} to={to} {...headingProps} />
        )}
        <ResultCardLinkSubHeading
          title={organisationName}
          {...subHeadingProps}
        />
      </ResultCardHeader>
      <ResultMetaList $listItemSpacing="3rem">
        {locations && (
          <ResultMetaListItem icon="marker">{locations}</ResultMetaListItem>
        )}
        {category && (
          <ResultMetaListItem icon="ribbon">{category}</ResultMetaListItem>
        )}
        {typesOfWork && (
          <ResultMetaListItem icon="briefcase">
            {typesOfWork}
          </ResultMetaListItem>
        )}
      </ResultMetaList>

      <ParseRemark>{summary}</ParseRemark>

      <ResultCardFooter>
        <BaseFavouriteBtn
          isFavourited={isFavourited}
          onFavourite={onFavourite}
          onUnfavourite={onUnfavourite}
          type={FAVOURITES_ENUM.OPPORTUNITY}
        />
        {logoUrl && (
          <img
            className="img-fluid my-2 ml-auto"
            srcSet={`${resizeImg(logoUrl, "180x120")} 2x`}
            src={resizeImg(logoUrl, "90x60")}
            alt={`Logo for ${organisationName}`}
          />
        )}
      </ResultCardFooter>
    </ResultCard>
  );
};

interface OrganisationDetailsOpportunityResultCardProps {
  name: React.ReactNode;
  to: ResultCardLinkHeadingProps["to"];

  locations?: React.ReactNode;
  category?: React.ReactNode;
  typesOfWork?: React.ReactNode;

  headingProps?: Partial<Parameters<typeof ResultCardLinkHeading>[0]>;
}

export const OrganisationDetailsOpportunityResultCard: React.FC<
  OrganisationDetailsOpportunityResultCardProps
> = ({ name, to, locations, category, typesOfWork, headingProps }) => {
  return (
    <ResultCard className="py-3">
      <ResultCardHeader>
        <ResultCardLinkHeading
          title={name}
          to={to}
          stretchedLink={false}
          className="h5 mb-0"
          {...headingProps}
        />
      </ResultCardHeader>
      <ResultMetaList>
        {locations && (
          <ResultMetaListItem icon="marker">{locations}</ResultMetaListItem>
        )}
        {category && (
          <ResultMetaListItem icon="ribbon">{category}</ResultMetaListItem>
        )}
        {typesOfWork && (
          <ResultMetaListItem icon="briefcase">
            {typesOfWork}
          </ResultMetaListItem>
        )}
      </ResultMetaList>
    </ResultCard>
  );
};
